import { useDebouncedSearch, useSearchOracleProjectsQuery } from '@consigli/hooks';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from '@consigli/shadcn';
import { UnchargeableReason } from '@consigli/types';
import { ChevronsUpDownIcon, Loader2Icon, XIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { Field, Fieldset } from '@/components';

import { CreateProjectFormData } from '../types';

const unchargeableLabel: Record<UnchargeableReason, string> = {
  project_status_does_not_allow_charges: 'Project status is not active',
  project_dates_do_not_allow_charges: 'Project dates do not allow charges',
  task_is_not_chargeable: 'Task is not chargeable',
  task_passed_active_end_date: 'Task has passed its active end date',
  transaction_control_does_not_allow_charges: 'Transaction controls do not allow charges',
};

interface OracleFieldsProps {
  formData: CreateProjectFormData;
  errors: Partial<Record<keyof CreateProjectFormData, string>>;
  onFieldChange: (field: keyof CreateProjectFormData, value: unknown) => void;
  onProjectNameResolved?: (name: string | null) => void;
}

export const OracleFields = ({
  formData,
  errors,
  onFieldChange,
  onProjectNameResolved,
}: OracleFieldsProps) => {
  const [projectOpen, setProjectOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedProjectName, setSelectedProjectName] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);

  const debouncedSearch = useDebouncedSearch(searchValue, 500);

  const {
    currentData: projectData,
    isLoading: isProjectLoading,
    isFetching: isProjectFetching,
    isError: isProjectError,
    error: projectError,
  } = useSearchOracleProjectsQuery(
    { search: debouncedSearch },
    { skip: !debouncedSearch || accessDenied },
  );

  const is403 = isProjectError && (projectError as { status?: number })?.status === 403;

  useEffect(() => {
    if (is403) setAccessDenied(true);
  }, [is403]);

  const projects = projectData?.results ?? [];

  const isDebouncing = Boolean(searchValue) && searchValue !== debouncedSearch;
  const isPending =
    Boolean(debouncedSearch) && projectData === undefined && !isProjectError && !accessDenied;
  const isSearchBusy = isProjectLoading || isProjectFetching || isDebouncing || isPending;

  const handleProjectSelect = useCallback(
    (projectNumber: string, projectName: string) => {
      setSelectedProjectName(projectName);
      onFieldChange('oracleProjectNumber', projectNumber);
      onProjectNameResolved?.(projectName);
      setProjectOpen(false);
      setSearchValue('');
    },
    [onFieldChange, onProjectNameResolved],
  );

  const handleProjectClear = useCallback(() => {
    setSelectedProjectName('');
    setSearchValue('');
    onFieldChange('oracleProjectNumber', undefined);
    onProjectNameResolved?.(null);
  }, [onFieldChange, onProjectNameResolved]);

  return (
    <Fieldset legend="Oracle">
      <Field
        label="Oracle Project (optional)"
        htmlFor="oracle-project"
        error={errors.oracleProjectNumber}
      >
        {formData.oracleProjectNumber ? (
          <div className="flex h-[40px] items-center justify-between rounded-md border border-input bg-background px-3 text-sm">
            <span className="min-w-0 flex-1 truncate">
              {selectedProjectName || formData.oracleProjectNumber}
              <span className="ml-1 text-muted-foreground">({formData.oracleProjectNumber})</span>
            </span>
            <button
              type="button"
              onClick={handleProjectClear}
              className="ml-2 shrink-0 text-muted-foreground hover:text-foreground"
              aria-label="Clear project"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <Popover open={projectOpen} onOpenChange={setProjectOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={projectOpen}
                className="h-[40px] w-full justify-between font-normal"
              >
                <span className="text-muted-foreground">Search Oracle projects...</span>
                <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-(--radix-popover-trigger-width) p-0">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search by name or number..."
                  value={searchValue}
                  onValueChange={setSearchValue}
                />
                <CommandList>
                  {isSearchBusy && (
                    <CommandEmpty>
                      <div className="flex items-center justify-center gap-2">
                        <Loader2Icon className="h-4 w-4 animate-spin" />
                        Searching...
                      </div>
                    </CommandEmpty>
                  )}
                  {isProjectError && (
                    <CommandEmpty>
                      {is403
                        ? 'Not authorized to search Oracle projects.'
                        : 'Search failed. Try again.'}
                    </CommandEmpty>
                  )}
                  {!isSearchBusy && !isProjectError && !debouncedSearch && (
                    <CommandEmpty>Type to search Oracle projects.</CommandEmpty>
                  )}
                  {!isSearchBusy && !isProjectError && debouncedSearch && projects.length === 0 && (
                    <CommandEmpty>No projects found.</CommandEmpty>
                  )}
                  {!isSearchBusy && projects.length > 0 && (
                    <CommandGroup>
                      {projects.map((project) => {
                        const disabled = !project.chargeable;
                        const reason = project.unchargeableReason
                          ? unchargeableLabel[project.unchargeableReason]
                          : '';
                        return (
                          <CommandItem
                            key={project.projectNumber}
                            value={project.projectNumber}
                            keywords={[project.projectName]}
                            disabled={disabled}
                            title={disabled ? reason : undefined}
                            onSelect={() =>
                              !disabled &&
                              handleProjectSelect(project.projectNumber, project.projectName)
                            }
                            className={cn(disabled && 'cursor-not-allowed opacity-50')}
                          >
                            <span className="font-medium">{project.projectNumber}</span>
                            <span className="mx-1 text-muted-foreground">—</span>
                            <span className="truncate">{project.projectName}</span>
                            {disabled && reason && (
                              <span className="ml-1 text-xs text-muted-foreground">({reason})</span>
                            )}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      </Field>
    </Fieldset>
  );
};
